import {useNavigate, useParams} from "react-router-dom";
import {Autocomplete, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import * as React from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {PaymentMethodService} from "crqlar-react-common/dist/services/billing/paymentMethod.service";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {PaymentMethod, PaymentMethodType} from "crqlar-react-common/dist/models/billing/paymentMethod.model";
import Property from "crqlar-react-common/dist/components/Property";
import ProfileInfoRow from "crqlar-react-common/dist/components/ProfileInfoRow";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import Button from "@mui/material/Button";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SettingsEntityHeader from "../../layout/SettingsEntityHeader";

const PaymentMethodDetails = () => {

    //region State

    useTrackScreenVisit('system payment method details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const paymentMethodData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return PaymentMethodService.getPaymentMethodById(id)
            } else {
                return {}
            }
        },
        create: PaymentMethodService.createPaymentMethod,
        update: PaymentMethodService.updatePaymentMethod,
        delete: (id) => PaymentMethodService.deletePaymentMethod({id: [id]}),
        onDeleted: () => navigate('/system/paymentMethods', {replace: true}),
        onSaved: (paymentMethod, wasCreateAction, prev) => {
            if (wasCreateAction) {
                navigate(`/system/paymentMethods/${paymentMethod.id}`, {replace: true})
            }
        },
    })

    const paymentMethod = paymentMethodData.item
    const setPaymentMethod = paymentMethodData.setItem


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof PaymentMethod, value: any) => {
        setPaymentMethod({...paymentMethod, [field]: value})
    }

    //endregion Handlers

    //region UI

    const titleSection = (
        <>
            <SettingsEntityHeader
                title={paymentMethod.id ? `#${paymentMethod.id}` : undefined}
                back={() => navigate('/system/paymentMethods')}
                data={paymentMethodData}
            />

            <Stack mb={2} direction={'row'} justifyContent={'flex-start'} sx={{display: {xs: 'none', md: 'flex'}}}>
                <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                >
                    {t('Last change at', {time: paymentMethod.updateDate?.toLocaleDateString() || '-'})}
                </Typography>
            </Stack>

        </>
    )

    const basicInfoSection = (
        <>
            <Typography variant={'h6'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Property
                title={t('Provider')}
                tooltip={t('Payment provider that created and manages this payment method.')}
            >
                <TextField
                    id={'name'}
                    size={'small'}
                    value={paymentMethod.provider}
                    onChange={(e) => updateField('provider', e.target.value)}
                    fullWidth
                    required
                    disabled
                />
            </Property>

            <Divider/>

            <Property
                title={t('Type')}
                tooltip={t('Payment method type reported by the provider.')}
            >
                <Autocomplete
                    id={'payment-type'}
                    options={[
                        undefined,
                        ...Object.keys(PaymentMethodType)
                    ]}
                    getOptionLabel={(option: any) => option || '(Not set)'}
                    value={paymentMethod.type?.toString() || ''}
                    fullWidth
                    onChange={(e, value) => {
                        updateField('type', value)
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            size={'small'}
                        />
                    )}
                    disabled
                />
            </Property>

            <Divider/>

            <ProfileInfoRow
                id={'profile'}
                profileId={paymentMethod.profileId}
                setProfileId={() => {
                }}
                disabled
            />

            <Divider/>

            <Property
                title={t('Folio')}
                tooltip={t('Folio this payment method is attached to, when available.')}
            >
                <>
                    <TextField
                        id={'reservation'}
                        size={'small'}
                        value={paymentMethod.folioId}
                        fullWidth
                        disabled
                    />

                    {
                        paymentMethod.folioId &&
                        <Button
                            id={'btn-view-folio'}
                            endIcon={<OpenInNewIcon/>}
                            sx={{minWidth: 'fit-content'}}
                            onClick={() => {
                                window.open(`/system/folios/${paymentMethod.folioId}`, '_blank')
                            }}
                        >
                            {t('View')}
                        </Button>
                    }

                </>
            </Property>

        </>
    )

    let cardSection = null
    if (paymentMethod.type === PaymentMethodType.CARD) {
        cardSection = (
            <>
                <Typography variant={'h6'} textAlign={'start'} mt={4}>
                    {t('Card data')}
                </Typography>

                <Property
                    title={t('Brand')}
                >
                    <TextField
                        id={'card-brand'}
                        size={'small'}
                        value={paymentMethod.card?.brand}
                        onChange={(e) => updateField('card', {
                            ...paymentMethod.card,
                            brand: e.target.value
                        })}
                        fullWidth
                        required
                        disabled
                    />
                </Property>

                <Divider/>

                <Property
                    title={t('Last 4')}
                >
                    <TextField
                        id={'card-last-4'}
                        size={'small'}
                        value={paymentMethod.card?.last4}
                        onChange={(e) => updateField('card', {
                            ...paymentMethod.card,
                            last4: e.target.value
                        })}
                        fullWidth
                        required
                        disabled
                    />
                </Property>

                <Divider/>

                <Property
                    title={t('Expiration (month/year)')}
                >
                    <Stack gap={2} direction={'row'} width={'100%'}>
                        <TextField
                            id={'card-exp-month'}
                            size={'small'}
                            value={paymentMethod.card?.expMonth}
                            onChange={(e) => updateField('card', {
                                ...paymentMethod.card,
                                expMonth: e.target.value
                            })}
                            fullWidth
                            required
                            disabled
                        />

                        <TextField
                            id={'card-exp-year'}
                            size={'small'}
                            value={paymentMethod.card?.expYear}
                            onChange={(e) => updateField('card', {
                                ...paymentMethod.card,
                                expYear: e.target.value
                            })}
                            fullWidth
                            required
                            disabled
                        />
                    </Stack>
                </Property>

                <Divider/>

                <Property
                    title={t('Funding')}
                >
                    <TextField
                        id={'card-funding'}
                        size={'small'}
                        value={paymentMethod.card?.funding}
                        onChange={(e) => updateField('card', {
                            ...paymentMethod.card,
                            funding: e.target.value
                        })}
                        fullWidth
                        required
                        disabled
                    />
                </Property>

            </>
        )
    }

    const billingDetails = (
        <>
            <Typography variant={'h6'} textAlign={'start'} mt={4}>
                {t('Billing details')}
            </Typography>

            <Property
                title={t('Name')}
            >
                <TextField
                    id={'billing-details-name'}
                    size={'small'}
                    value={paymentMethod.billingDetails?.name}
                    onChange={(e) => updateField('billingDetails', {
                        ...paymentMethod.billingDetails,
                        name: e.target.value
                    })}
                    fullWidth
                    required
                    disabled
                />
            </Property>

            <Divider/>

            <Property
                title={t('Email')}
            >
                <TextField
                    id={'billing-details-email'}
                    size={'small'}
                    value={paymentMethod.billingDetails?.email}
                    onChange={(e) => updateField('billingDetails', {
                        ...paymentMethod.billingDetails,
                        email: e.target.value
                    })}
                    fullWidth
                    required
                    disabled
                />
            </Property>

            <Divider/>

            <Property
                title={t('Phone')}
            >
                <TextField
                    id={'billing-details-phone'}
                    size={'small'}
                    value={paymentMethod.billingDetails?.phone}
                    onChange={(e) => updateField('billingDetails', {
                        ...paymentMethod.billingDetails,
                        phone: e.target.value
                    })}
                    fullWidth
                    required
                    disabled
                />
            </Property>

        </>
    )


    //endregion UI

    const paymentMethodDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                gap: 1,
                alignSelf: 'center'
            }}
        >
            {titleSection}
            {basicInfoSection}
            {cardSection}
            {billingDetails}
        </Stack>
    )

    return paymentMethodDataUI;
}

export default PaymentMethodDetails;
